import { useEffect, useRef, useState } from 'react';

/**
 * Upper bound on a "hold the screen until the reads settle" wait.
 *
 * Long enough that a slow-but-working load still resolves on its own and nobody sees the
 * fallback (the home's reads are all sub-second in practice, and the p99 of the slowest of
 * them is comfortably inside this); short enough that a wait which is never going to end
 * stops being the user's whole experience of the product.
 */
export const HOLD_DEADLINE_MS = 10_000;

/**
 * `true` once a hold has been active continuously for {@link HOLD_DEADLINE_MS}.
 *
 * Pass the hold's own condition: the timer starts when the hold begins and is cancelled the
 * moment it lifts, so a normal load never arms it to completion and this stays `false` for
 * the entire life of the screen. Once expired it LATCHES — a hold that lifts and re-arms
 * (a refetch, a duplexer nudge) must not be able to put the loader back over a screen the
 * user is already reading.
 *
 * This is a floor under the user experience, not a fix for whatever is stalling: the read that
 * never settled is still broken and still worth finding. What this guarantees is that the
 * failure surfaces as a screen instead of as an indefinite spinner.
 */
export function useHoldDeadline(holding: boolean, deadlineMs: number = HOLD_DEADLINE_MS): boolean {
  const [expired, setExpired] = useState(false);
  // Read in the effect but deliberately NOT a dependency: adding it would tear down and
  // restart the timer on the render that sets it, which is a re-arm, not a reset.
  const expiredRef = useRef(false);
  expiredRef.current = expired;

  useEffect(() => {
    if (!holding || expiredRef.current) return;
    const timer = setTimeout(() => setExpired(true), deadlineMs);
    return () => clearTimeout(timer);
  }, [holding, deadlineMs]);

  return expired;
}
